import { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { getStoredUser, clearSession, usersApi, type PendingReviews } from '../api/client.js';

const navItems = [
  { path: '/', label: '仪表板', desc: '经营全景概览', icon: '📊' },
  { path: '/accounts', label: '会计科目', desc: '科目体系维护', icon: '📒' },
  { path: '/parties', label: '往来单位', desc: '客户/供应商档案', icon: '🤝' },
  { path: '/journal', label: '记账凭证', desc: '凭证录入与审核', icon: '📝' },
  { path: '/import', label: '智能导入', desc: '流水一键入账', icon: '📥' },
  { path: '/wechat', label: '收款分类确认', desc: 'AI 智能分类', icon: '💬' },
  { path: '/monitor', label: '企微监管', desc: '会话信号洞察', icon: '🛡️' },
  { path: '/reports', label: '报表中心', desc: '账簿与报表', icon: '📈' },
  { path: '/closing', label: '期末结账', desc: '损益结转与锁定', icon: '🔒' },
  { path: '/team', label: '团队成员', desc: '成员与数据归属', icon: '👥', adminOnly: true },
  { path: '/admin', label: '租户运维', desc: '配额与数据归档', icon: '🗂️', adminOnly: true },
];

const BUSINESS_LABELS: Record<string, string> = {
  consulting: '咨询费',
  design: '设计费',
  project: '项目款',
  expense: '费用支出',
  training: '培训费',
};

export function formatBusinessType(type?: string | null): string {
  if (!type) return '未分类';
  return BUSINESS_LABELS[type] || type;
}

export function formatAmount(n: number): string {
  return n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const ROLE_LABELS: Record<string, string> = {
  admin: '管理员',
  senior_accountant: '主管会计',
  accountant: '会计',
  viewer: '浏览员',
};

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getStoredUser();

  const logout = () => {
    clearSession();
    navigate('/login', { replace: true });
  };

  const current =
    navItems.find((i) => (i.path === '/' ? location.pathname === '/' : location.pathname.startsWith(i.path)))
    || navItems[0];

  const visibleNav = navItems.filter((i) => !('adminOnly' in i && i.adminOnly) || user?.role === 'admin');

  // 8.4.12 待处理复核徽标：仅 admin，挂载时拉一次 + 60s 轮询；失败静默（侧栏为被动提示不打扰主流程）
  const [pending, setPending] = useState<PendingReviews | null>(null);
  useEffect(() => {
    if (user?.role !== 'admin') return;
    let alive = true;
    const load = () => usersApi.pendingReviews().then((d) => { if (alive) setPending(d); }).catch(() => {});
    load();
    const timer = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(timer); };
  }, [user?.role]);

  const initial = (user?.displayName || user?.username || '访').slice(0, 1).toUpperCase();

  return (
    <div className="min-h-screen flex">
      {/* 侧边栏 */}
      <aside className="w-64 bg-sidebar-bg text-white flex flex-col fixed inset-y-0 left-0 z-30 shadow-glow">
        {/* 品牌 */}
        <div className="flex items-center gap-3 px-5 h-16 border-b border-white/10">
          <div className="w-9 h-9 rounded-xl bg-brand-gradient shadow-glow flex items-center justify-center text-lg font-black">
            Z
          </div>
          <div className="leading-tight">
            <p className="font-bold text-[15px] tracking-wide">ZInternal</p>
            <p className="text-[11px] text-slate-400">AI 智能内账系统</p>
          </div>
        </div>

        {/* 导航 */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto no-scrollbar">
          {visibleNav.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              className={({ isActive }) =>
                `group relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all duration-200 ${
                  isActive
                    ? 'bg-white/10 text-white shadow-inset'
                    : 'text-slate-400 hover:bg-white/5 hover:text-slate-100'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-1 rounded-r-full bg-brand-gradient" />
                  )}
                  <span
                    className={`grid place-items-center w-8 h-8 rounded-lg text-base transition-colors ${
                      isActive ? 'bg-brand-gradient shadow-glow-sm' : 'bg-white/5 group-hover:bg-white/10'
                    }`}
                  >
                    {item.icon}
                  </span>
                  <span className="font-medium">{item.label}</span>
                  {item.path === '/team' && (pending?.pending ?? 0) > 0 && (
                    <span
                      title={pending && pending.overdue > 0
                        ? `可疑待核未销案 ${pending.pending} 条，其中超 ${pending.days} 天 ${pending.overdue} 条`
                        : `可疑待核未销案 ${pending!.pending} 条`}
                      className="ml-auto shrink-0 min-w-[1.25rem] h-5 px-1.5 rounded-full bg-amber-500/90 text-white text-[11px] font-bold grid place-items-center"
                    >
                      {pending!.pending}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* 底部：租户与版本 */}
        <div className="px-4 py-3 border-t border-white/10">
          <div className="flex items-center justify-between text-[11px] text-slate-400">
            <span>试点版 v0.1</span>
            {user ? <span className="truncate max-w-[9rem]">租户 · {user.tenantId}</span> : <span>匿名模式</span>}
          </div>
        </div>
      </aside>

      {/* 主区 */}
      <div className="flex-1 ml-64 flex flex-col min-h-screen">
        {/* 顶栏 */}
        <header className="sticky top-0 z-20 h-16 glass flex items-center justify-between px-8">
          <div className="leading-tight">
            <h1 className="text-[15px] font-bold text-slate-900">{current.label}</h1>
            <p className="text-xs text-slate-500">{current.desc}</p>
          </div>

          {user ? (
            <div className="flex items-center gap-3">
              <div className="text-right leading-tight hidden sm:block">
                <p className="text-sm font-semibold text-slate-800">{user.displayName}</p>
                <p className="text-[11px] text-slate-500">{ROLE_LABELS[user.role] || user.role}</p>
              </div>
              <div className="w-9 h-9 rounded-full bg-brand-gradient text-white grid place-items-center text-sm font-bold shadow-glow-sm">
                {initial}
              </div>
              <button onClick={logout} className="btn btn-ghost btn-sm" title="退出登录">
                退出
              </button>
            </div>
          ) : (
            <NavLink to="/login" className="btn btn-primary btn-sm">
              登录
            </NavLink>
          )}
        </header>

        {/* 内容 */}
        <main className="flex-1 p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
